import React, { useState } from 'react';
import { Button, Card, Divider, Flex, Typography, theme } from 'antd';
import {
  ArrowRightOutlined,
  CaretDownOutlined,
  CaretUpOutlined,
  InfoCircleOutlined,
  QuestionCircleOutlined,
} from '@ant-design/icons';

import { CustomTooltip } from '~components/Tooltip';

import {
  formatMissingCrawlers,
  MISSING_CRAWLERS_TOOLTIP,
  MissingCrawlerDomain,
  useAiCrawlerLineup,
} from '../../shared';

type MissingCrawlersCardProps = {
  totalAffectedDomains: number;
  integratedDomains: string[];
  totalMissingCrawlers: number;
  domains: MissingCrawlerDomain[];
  days: number;
  onRunDiagnostics: (domain: string) => void;
};

const INITIAL_VISIBLE_COUNT = 2;

// Single domain -> name it directly ("on prerender.io"); multiple domains ->
// count them ("across 3 domains"). Per the PRD, "across 1 domain" must never
// render (PRE-3137).
const domainScopeSuffix = (count: number, singleDomainName?: string): string =>
  count === 1 && singleDomainName
    ? `on ${singleDomainName}`
    : `across ${count} ${count === 1 ? 'domain' : 'domains'}`;

export default function MissingCrawlersCard({
  totalAffectedDomains,
  integratedDomains,
  totalMissingCrawlers,
  domains,
  days,
  onRunDiagnostics,
}: MissingCrawlersCardProps) {
  const [expanded, setExpanded] = useState(false);
  const { token } = theme.useToken();
  const lineup = useAiCrawlerLineup();
  const trackedCount = lineup.providers.length;

  const allDetected = totalMissingCrawlers === 0;
  const allMissing = !allDetected && totalMissingCrawlers === trackedCount;
  const visibleDomains = expanded ? domains : domains.slice(0, INITIAL_VISIBLE_COUNT);
  const hiddenCount = domains.length - INITIAL_VISIBLE_COUNT;

  const headline = allDetected
    ? `All ${trackedCount} AI Crawlers detected ${domainScopeSuffix(integratedDomains.length, integratedDomains[0])}`
    : allMissing
      ? `All ${trackedCount} AI Crawlers not detected ${domainScopeSuffix(totalAffectedDomains, domains[0]?.domain)}`
      : `We didn't detect ${totalMissingCrawlers} of ${trackedCount} AI Crawlers ${domainScopeSuffix(totalAffectedDomains, domains[0]?.domain)}`;

  return (
    <Card styles={{ body: { padding: token.paddingMD } }}>
      <Flex vertical gap={token.marginSM}>
        <div>
          <Flex align="center" gap={token.marginXXS}>
            <Typography.Text strong style={{ fontSize: token.fontSizeLG }}>
              {headline}
            </Typography.Text>
            {!allDetected && (
              <CustomTooltip placement="top" message={MISSING_CRAWLERS_TOOLTIP}>
                <InfoCircleOutlined style={{ fontSize: token.fontSizeLG, color: token.colorTextSecondary, cursor: 'pointer' }} />
              </CustomTooltip>
            )}
          </Flex>
          <Typography.Text type="secondary" style={{ fontSize: token.fontSizeSM }}>
            {`Updates daily based on the last ${days} days of AI Crawler activity.`}
          </Typography.Text>
        </div>

        {visibleDomains.map((item) => (
          <React.Fragment key={item.domain}>
            <Divider style={{ margin: 0 }} />
            <Flex align="center" gap={token.marginXS}>
              <QuestionCircleOutlined style={{ fontSize: token.fontSizeLG, color: token.colorWarning }} />
              <Typography.Text style={{ flex: 1, fontSize: token.fontSize }}>
                <Typography.Text strong>{item.domain}: </Typography.Text>
                {formatMissingCrawlers(lineup, item.missingCrawlers)} missing
              </Typography.Text>
              <Button
                type="link"
                icon={<ArrowRightOutlined />}
                style={{ color: token.colorPrimary, padding: `0 ${token.marginXXS}px` }}
                onClick={() => onRunDiagnostics(item.domain)}
              >
                Run Diagnostics
              </Button>
            </Flex>
          </React.Fragment>
        ))}

        {hiddenCount > 0 && (
          <>
            <Divider style={{ margin: 0 }} />
            <Flex
              align="center"
              gap={token.marginSM}
              style={{ cursor: 'pointer' }}
              onClick={() => setExpanded((prev) => !prev)}
            >
              {expanded ? (
                <CaretUpOutlined style={{ fontSize: token.fontSizeLG, color: token.colorPrimary }} />
              ) : (
                <CaretDownOutlined style={{ fontSize: token.fontSizeLG, color: token.colorPrimary }} />
              )}
              <Typography.Text style={{ fontSize: token.fontSize, color: token.colorPrimary }}>
                {expanded ? 'Show less' : (
                  <>Show <Typography.Text strong style={{ color: token.colorPrimary }}>{hiddenCount} more</Typography.Text> affected {hiddenCount === 1 ? 'domain' : 'domains'}</>
                )}
              </Typography.Text>
            </Flex>
          </>
        )}
      </Flex>
    </Card>
  );
}
